import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import {
  AgoraRTCProvider,
  LocalUser,
  RemoteUser,
  useJoin,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  usePublish,
  useRemoteUsers,
  useClientEvent,
  IAgoraRTCClient,
} from 'agora-rtc-react';
import { MeetingTokenResponse } from '../../services/api/meetingService';
import { meetingService } from '../../services/api/meetingService';
import { LeaveConfirmationModal } from './LeaveConfirmationModal';
import './MeetingRoom.scss';

interface MeetingRoomProps {
  initial: MeetingTokenResponse;
  onLeave?: () => void;
  onError?: (message: string) => void;
  displayMode?: 'modal' | 'page';
}

// Create client outside component to avoid recreation on re-renders
const agoraClient = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });

export function MeetingRoom(props: MeetingRoomProps) {
  return (
    <AgoraRTCProvider client={agoraClient as any}>
      <RoomInner {...props} client={agoraClient as any} />
    </AgoraRTCProvider>
  );
}

interface RoomInnerProps extends MeetingRoomProps {
  client: IAgoraRTCClient;
}

function RoomInner({
  initial,
  onLeave,
  onError,
  displayMode = 'page',
  client,
}: RoomInnerProps) {
  const [auth, setAuth] = useState(initial);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const isMounted = useRef(true);
  const isJoiningRef = useRef(false);
  const renewTimer = useRef<number | null>(null);

  // Convert UID to number for consistency
  const uid = typeof auth.uid === 'string' ? parseInt(auth.uid, 10) : auth.uid;

  console.log('[MeetingRoom] UID type check:', {
    original: auth.uid,
    converted: uid,
    type: typeof uid,
    isNumber: typeof uid === 'number'
  });

  // Join channel first
  const joinState = useJoin(
    {
      appid: auth.appId,
      channel: auth.channelName,
      token: auth.token,
      uid: uid,
    },
    true
  );

  // Create tracks manually to avoid agora-rtc-react hook issues
  const [localMicrophoneTrack, setLocalMicrophoneTrack] = useState<any>(null);
  const [localCameraTrack, setLocalCameraTrack] = useState<any>(null);
  const remoteUsers = useRemoteUsers();

  // Create tracks after join succeeds
  useEffect(() => {
    if (!joinState.isConnected) return;

    const createTracks = async () => {
      try {
        console.log('[MeetingRoom] Creating tracks...');
        const [micTrack, cameraTrack] = await Promise.all([
          AgoraRTC.createMicrophoneAudioTrack({ encoderConfig: "music_standard" }),
          AgoraRTC.createCameraVideoTrack()
        ]);

        setLocalMicrophoneTrack(micTrack);
        setLocalCameraTrack(cameraTrack);
        console.log('[MeetingRoom] Tracks created successfully');
      } catch (err) {
        console.error('[MeetingRoom] Failed to create tracks:', err);
        onError?.('Failed to access camera/microphone');
      }
    };

    createTracks();
  }, [joinState.isConnected, onError]);

  // Track video availability for debugging
  useEffect(() => {
    if (localCameraTrack && camOn) {
      console.log('[MeetingRoom] Local camera track available and enabled');
    }
  }, [localCameraTrack, camOn]);

  // Log join state
  useEffect(() => {
    console.log('[MeetingRoom] Join state:', {
      isLoading: joinState.isLoading,
      isConnected: joinState.isConnected,
      error: joinState.error,
    });

    if (joinState.isConnected) {
      console.log('[MeetingRoom] ✅ Successfully joined channel:', {
        channel: auth.channelName,
        uid: uid,
        appId: auth.appId,
      });
    }
  }, [joinState, auth.channelName, uid, auth.appId]);

  // Manually publish tracks (avoid usePublish hook bug with data channel IDs)
  const hasPublishedRef = useRef(false);

  useEffect(() => {
    const publishTracks = async () => {
      if (!joinState.isConnected) {
        hasPublishedRef.current = false;
        return;
      }

      if (!localMicrophoneTrack || !localCameraTrack) {
        return;
      }

      if (hasPublishedRef.current) {
        return; // Already published
      }

      try {
        console.log('[MeetingRoom] Publishing tracks...');
        await client.publish([localMicrophoneTrack, localCameraTrack]);
        hasPublishedRef.current = true;
        console.log('[MeetingRoom] ✅ Successfully published tracks');
      } catch (err) {
        console.error('[MeetingRoom] ❌ Publish error:', err);
        onError?.('Failed to publish media tracks');
      }
    };

    publishTracks();
  }, [joinState.isConnected, localMicrophoneTrack, localCameraTrack, client, onError]);

  // Log when user joins
  useClientEvent(client, 'user-joined', (user) => {
    console.log('[MeetingRoom] 👤 User joined channel:', user.uid);
  });

  // Log when user leaves
  useClientEvent(client, 'user-left', (user, reason) => {
    console.log('[MeetingRoom] 👋 User left channel:', user.uid, 'reason:', reason);
  });

  // Auto-subscribe to remote users
  useClientEvent(client, 'user-published', async (user, mediaType) => {
    console.log('[MeetingRoom] 📡 User published:', user.uid, mediaType, {
      hasVideo: user.hasVideo,
      hasAudio: user.hasAudio,
      videoTrack: !!user.videoTrack,
      audioTrack: !!user.audioTrack
    });
    try {
      await client.subscribe(user, mediaType);
      console.log('[MeetingRoom] ✅ Successfully subscribed to:', user.uid, mediaType);

      // Auto-play audio
      if (mediaType === 'audio' && user.audioTrack) {
        user.audioTrack.play();
        console.log('[MeetingRoom] 🔊 Playing audio from:', user.uid);
      }
    } catch (err) {
      console.error('[MeetingRoom] ❌ Failed to subscribe:', user.uid, mediaType, err);
    }
  });

  // Log when user unpublishes
  useClientEvent(client, 'user-unpublished', (user, mediaType) => {
    console.log('[MeetingRoom] 📴 User unpublished:', user.uid, mediaType);
  });

  // Log remote users for debugging
  useEffect(() => {
    console.log('[MeetingRoom] Remote users:', remoteUsers.length, remoteUsers.map(u => ({
      uid: u.uid,
      hasVideo: u.hasVideo,
      hasAudio: u.hasAudio,
      videoTrack: !!u.videoTrack,
      audioTrack: !!u.audioTrack
    })));
  }, [remoteUsers]);

  // Token renewal - proactive (60s before expiry)
  useEffect(() => {
    const expiresAt = new Date(auth.tokenExpiresAt).getTime();
    const msUntilExpiry = expiresAt - Date.now();
    const renewLead = 60_000; // renew one minute before expiry
    const timeout = Math.max(5_000, msUntilExpiry - renewLead);

    if (Number.isFinite(timeout)) {
      renewTimer.current = window.setTimeout(() => {
        renewToken().catch(() => undefined);
      }, timeout);
    }

    return () => {
      if (renewTimer.current) {
        window.clearTimeout(renewTimer.current);
      }
    };
  }, [auth.token, auth.tokenExpiresAt]);

  // Token renewal - event-driven
  useClientEvent(client, 'token-privilege-will-expire', () => {
    console.log('[MeetingRoom] Token will expire soon, renewing...');
    renewToken().catch(() => undefined);
  });

  useClientEvent(client, 'token-privilege-did-expire', async () => {
    console.log('[MeetingRoom] Token expired, rejoining...');
    try {
      const newAuth = await fetchNewToken();
      const renewUid =
        typeof newAuth.uid === 'string' ? parseInt(newAuth.uid, 10) : newAuth.uid;
      await client.join(newAuth.appId, newAuth.channelName, newAuth.token, renewUid);
      setAuth(newAuth);
    } catch (err) {
      console.error('Failed to rejoin meeting after token expiry', err);
    }
  });

  // Fetch new token
  const fetchNewToken = async (): Promise<MeetingTokenResponse> => {
    try {
      const token = await meetingService.generateToken({
        meeting_id: auth.channelName,
        patient: 1,
      });
      if (isMounted.current) {
        setError(null);
      }
      return token;
    } catch (err) {
      console.error('Failed to fetch new meeting token', err);
      const message =
        err instanceof Error ? err.message : 'Failed to renew meeting token.';
      if (isMounted.current) {
        setError(message);
      }
      onError?.(message);
      throw err;
    }
  };

  // Renew token
  const renewToken = async () => {
    try {
      const fresh = await fetchNewToken();
      await client.renewToken(fresh.token);
      if (isMounted.current) {
        setAuth(fresh);
      }
    } catch (err) {
      console.error('Failed to renew Agora token', err);
      const message = err instanceof Error ? err.message : 'Token renewal failed.';
      if (isMounted.current) {
        setError(message);
      }
      onError?.(message);
      throw err;
    }
  };

  // Toggle microphone
  const toggleMic = useCallback(() => {
    if (localMicrophoneTrack) {
      const newState = !micOn;
      localMicrophoneTrack.setEnabled(newState);
      setMicOn(newState);
    }
  }, [localMicrophoneTrack, micOn]);

  // Toggle camera
  const toggleCam = useCallback(() => {
    if (localCameraTrack) {
      const newState = !camOn;
      localCameraTrack.setEnabled(newState);
      setCamOn(newState);
    }
  }, [localCameraTrack, camOn]);

  // Handle leave
  const handleLeaveClick = useCallback(() => {
    setShowLeaveModal(true);
  }, []);

  const handleLeaveConfirm = useCallback(() => {
    setShowLeaveModal(false);
    onLeave?.();
  }, [onLeave]);

  const handleLeaveCancel = useCallback(() => {
    setShowLeaveModal(false);
  }, []);

  // ESC key to leave
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleLeaveClick();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [handleLeaveClick]);

  // Cleanup on unmount ONLY
  useEffect(() => {
    isJoiningRef.current = true;
    isMounted.current = true;

    return () => {
      isMounted.current = false;
      isJoiningRef.current = false;
      hasPublishedRef.current = false;

      if (renewTimer.current) {
        window.clearTimeout(renewTimer.current);
      }

      // Clean up tracks and leave channel
      const cleanup = async () => {
        try {
          // Unpublish first
          await client.unpublish().catch(() => {});

          // Close tracks
          const tracks = client.localTracks;
          tracks.forEach(track => {
            track.close();
          });

          // Leave channel
          await client.leave();
        } catch (err) {
          console.log('[MeetingRoom] Cleanup error (expected):', err);
        }
      };

      cleanup();
    };
  }, [client]); // Only depend on client, not tracks!

  return (
    <div className={`meeting-room meeting-room--${displayMode}`}>
      <div className="meeting-room__header">
        <div className="meeting-room__header-left">
          <img src="/aigp-logo.png" alt="AIGP Health" className="meeting-room__logo" />
          <div className="meeting-room__header-info">
            <h2 className="meeting-room__title">Video Consultation</h2>
            <p className="meeting-room__subtitle">
              {remoteUsers.length > 0
                ? `Connected with doctor`
                : 'Waiting for doctor to join...'}
            </p>
          </div>
        </div>
      </div>

      <div className="meeting-room__content">
        {/* Error banner */}
        {error && (
          <div className="meeting-room__error-banner">
            <span>{error}</span>
          </div>
        )}

        {/* Participants grid */}
        <div className="meeting-room__participants">
          {/* Local user (patient) */}
          <div className="local-participant">
            {localCameraTrack && camOn ? (
              <div className="local-video-wrapper">
                <video
                  autoPlay
                  playsInline
                  ref={(videoElement) => {
                    if (videoElement && localCameraTrack) {
                      // Use the track's play method to render to video element
                      localCameraTrack.play(videoElement);
                    }
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                <div className="participant-label">You</div>
              </div>
            ) : camOn ? (
              // Fallback to LocalUser component if track is available
              <LocalUser
                audioTrack={localMicrophoneTrack}
                videoTrack={localCameraTrack}
                cameraOn={camOn}
                micOn={micOn}
              >
                <div className="participant-label">You</div>
              </LocalUser>
            ) : (
              // Camera off placeholder
              <div className="camera-off-placeholder">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                  <path d="M21 21H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3m3-3h6l2 3h4a2 2 0 0 1 2 2v9.34m-7.72-2.06a4 4 0 1 1-5.56-5.56"></path>
                </svg>
                <div className="participant-label">Camera Off</div>
              </div>
            )}
          </div>

          {/* Remote users (doctor) */}
          {remoteUsers.map((user) => (
            <div className="remote-participant" key={user.uid}>
              <RemoteUser
                user={user}
                playVideo={true}
                playAudio={true}
                cover="contain"
              />
              <div className="participant-label">Doctor</div>
            </div>
          ))}

          {/* Waiting message */}
          {!remoteUsers.length && (
            <div className="waiting-room">
              <div className="waiting-room__spinner"></div>
              <p>Waiting for doctor to join…</p>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="meeting-room__controls">
          <button
            className={`control-btn ${micOn ? 'active' : 'inactive'}`}
            onClick={toggleMic}
            title={micOn ? 'Mute' : 'Unmute'}
          >
            {micOn ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                <line x1="12" y1="19" x2="12" y2="23"></line>
                <line x1="8" y1="23" x2="16" y2="23"></line>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="1" y1="1" x2="23" y2="23"></line>
                <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
                <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path>
                <line x1="12" y1="19" x2="12" y2="23"></line>
                <line x1="8" y1="23" x2="16" y2="23"></line>
              </svg>
            )}
          </button>

          <button
            className={`control-btn ${camOn ? 'active' : 'inactive'}`}
            onClick={toggleCam}
            title={camOn ? 'Turn off camera' : 'Turn on camera'}
          >
            {camOn ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M23 7l-7 5 7 5V7z"></path>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="1" y1="1" x2="23" y2="23"></line>
                <path d="M21 21H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3m3-3h6l2 3h4a2 2 0 0 1 2 2v9.34m-7.72-2.06a4 4 0 1 1-5.56-5.56"></path>
              </svg>
            )}
          </button>

          <button className="control-btn leave-btn" onClick={handleLeaveClick} title="Leave meeting">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M18.36 6.64a9 9 0 1 1-12.73 0"></path>
              <line x1="12" y1="2" x2="12" y2="12"></line>
            </svg>
            <span>Leave</span>
          </button>
        </div>
      </div>

      {/* Leave confirmation modal */}
      <LeaveConfirmationModal
        isOpen={showLeaveModal}
        onConfirm={handleLeaveConfirm}
        onCancel={handleLeaveCancel}
      />
    </div>
  );
}
